import { FC, useState } from 'react'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useAppSelector } from '../../../../../store/hooks'
import SortableSection from './SortableSection'
import CanvasHeader from './CanvasHeader'
import { BuilderCanvasProps, BuilderGridItem } from '../types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faGripVertical,
    faLayerGroup,
    faBars,
} from '@fortawesome/free-solid-svg-icons'

const DEVICE_WIDTHS: Record<string, string> = {
    desktop: '100%',
    tablet: '768px',
    mobile: '375px',
}

const BuilderCanvas: FC<BuilderCanvasProps> = ({
    sections,
    selectedSectionId,
    onSelectSection,
    onReorder,
    onEditSection,
    onRemoveSection,
    onToggleActive,
    onContentChange,
    onImageChange,
    onNameChange,
    devicePreview,
    page,
    previewTheme,
    formikProps,
    availablePages,
    deletedCtas,
    setDeletedCtas,
    headerSizeCodes,
    translations,
    activeLocale,
}) => {
    const theme = useAppSelector((state) => state.theme.theme)
    const [activeId, setActiveId] = useState<string | null>(null)

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string)
    }

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveId(null)
        const { active, over } = event

        if (over && active.id !== over.id) {
            const oldIndex = sections.findIndex((s) => s.id === active.id)
            const newIndex = sections.findIndex((s) => s.id === over.id)
            const reordered = arrayMove(sections, oldIndex, newIndex)
            onReorder(reordered)
        }
    }

    const handleDragCancel = () => {
        setActiveId(null)
    }

    const activeItem = activeId
        ? sections.find((s) => s.id === activeId)
        : null

    const sectionIds = sections.map((s) => s.id)
    const showBurger = devicePreview !== 'desktop'

    return (
        <div
            className={`sfy-builder-canvas ${
                theme === 'dark' ? 'sfy-builder-canvas--dark' : ''
            }`}
        >
            <div
                className="sfy-builder-canvas__viewport"
                style={{
                    maxWidth: DEVICE_WIDTHS[devicePreview],
                }}
            >
                <div className="sfy-builder-canvas__device-frame">
                    <div className="sfy-builder-canvas__browser-bar">
                        <div className="sfy-builder-canvas__browser-dots">
                            <span />
                            <span />
                            <span />
                        </div>
                        <div className="sfy-builder-canvas__browser-url">
                            <span>/{page.code || 'page-url'}</span>
                        </div>
                    </div>

                    <div className="sfy-builder-canvas__content">
                        <div className={`sfy-canvas-nav ${showBurger ? 'sfy-canvas-nav--compact' : ''}`}>
                            <div className="sfy-canvas-nav__inner">
                                <div className="sfy-canvas-nav__logo">
                                    <span className="sfy-canvas-nav__logo-text">
                                        {page.page_title || page.name || 'Site'}
                                    </span>
                                </div>
                                {showBurger ? (
                                    <button type="button" className="sfy-canvas-nav__burger" aria-label="Menu">
                                        <FontAwesomeIcon icon={faBars} />
                                    </button>
                                ) : (
                                    <div className="sfy-canvas-nav__links">
                                        <span>Home</span>
                                        <span>About</span>
                                        <span>Blog</span>
                                        <span>Contact</span>
                                        <span className="sfy-canvas-nav__cta-btn">Donate</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <CanvasHeader
                            page={page}
                            previewTheme={previewTheme}
                            formikProps={formikProps}
                            availablePages={availablePages}
                            deletedCtas={deletedCtas}
                            setDeletedCtas={setDeletedCtas}
                            headerSizeCodes={headerSizeCodes}
                            translations={translations}
                            activeLocale={activeLocale}
                            devicePreview={devicePreview}
                        />

                        {sections.length > 0 ? (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                                onDragCancel={handleDragCancel}
                            >
                                <SortableContext
                                    items={sectionIds}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="sfy-builder-canvas__sections">
                                        {sections.map((section, index) => (
                                            <SortableSection
                                                key={section.id}
                                                item={section}
                                                index={index}
                                                isSelected={
                                                    selectedSectionId ===
                                                    section.id
                                                }
                                                onSelect={onSelectSection}
                                                onEdit={onEditSection}
                                                onRemove={onRemoveSection}
                                                onToggleActive={onToggleActive}
                                                onContentChange={onContentChange}
                                                onImageChange={onImageChange}
                                                onNameChange={onNameChange}
                                                activeLocale={activeLocale}
                                                devicePreview={devicePreview}
                                                formikProps={formikProps}
                                                translations={translations}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>

                                <DragOverlay dropAnimation={{
                                    duration: 250,
                                    easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                                }}>
                                    {activeItem ? (
                                        <div className="sfy-drag-overlay">
                                            <div className="sfy-drag-overlay__handle">
                                                <FontAwesomeIcon icon={faGripVertical} />
                                            </div>
                                            <div className="sfy-drag-overlay__info">
                                                <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                                                <span>
                                                    {(activeItem.pagePart as { page_content_name?: string })
                                                        ?.page_content_name || 'Section'}
                                                </span>
                                            </div>
                                        </div>
                                    ) : null}
                                </DragOverlay>
                            </DndContext>
                        ) : (
                            <div className="sfy-builder-canvas__empty">
                                <FontAwesomeIcon
                                    icon={faLayerGroup}
                                    className="sfy-builder-canvas__empty-icon"
                                />
                                <p>No sections yet</p>
                                <span>Add your first section using the button in the toolbar</span>
                            </div>
                        )}

                        <div className="sfy-canvas-footer">
                            <span>&copy; {new Date().getFullYear()} {page.page_title || page.name || 'Your Site'}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BuilderCanvas
